import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { configureApiBase, retryDelay, retryPolicy, useAuth } from '@fieldvision/web-core';
import { LucideProvider } from 'lucide-react';
import { BrowserRouter } from 'react-router-dom';

import { App } from './App.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { Toaster } from './components/ui/Toaster.js';
import { feedbackMutationCache } from './lib/mutation-feedback.js';
import { resolveApiBase } from './runtime-config.js';
import './index.css';

// Must run before anything can issue a request. @fieldvision/web-core is
// compiled to dist/ and cannot read this app's Vite env itself.
//
// Resolved at runtime from /config.js, falling back to the build-time value —
// which is what lets this image be promoted rather than rebuilt. See
// ./runtime-config.ts.
configureApiBase(resolveApiBase());

/**
 * How long we are willing to keep a spinner on screen rather than an error, when
 * the API has stopped answering entirely.
 *
 * Sized against a restart of the api component, which is what actually causes
 * this. It runs at `api_instance_count = 1` in production, so there is no second
 * instance to take the traffic: the process goes away, Nest boots, and for that
 * window every request gets a gateway error from the platform — which the
 * browser reports as a CORS failure, because an error page carries no CORS
 * headers. Reported as "the console randomly says CORS and a refresh fixes it".
 *
 * Roughly 35 seconds across six attempts. The manager waits instead of reading
 * an error about a condition that resolves itself and that a manual reload was
 * curing anyway.
 */
const UNREACHABLE_ATTEMPTS = 6;

const queryClient = new QueryClient({
  /*
   * WRITES REPORT THEMSELVES, from here rather than from each call site — see
   * `lib/mutation-feedback.ts` for why the default is feedback and the opt-out
   * is the exception.
   */
  mutationCache: feedbackMutationCache(),
  defaultOptions: {
    queries: {
      /*
       * RETRIES ARE CHOSEN BY WHAT FAILED, not by a single count — the rule and
       * its reasoning live in `@fieldvision/core`, because the staff app faces
       * the same api and the same restarts.
       *
       * `retry: 1` treated all three cases the same: a 404 that is settled, a
       * 401 that has already been through the client's refresh-and-sign-out
       * path, and a request that got no answer at all. Only the last is worth
       * repeating, and one attempt does not cover a restart.
       */
      retry: retryPolicy(UNREACHABLE_ATTEMPTS),
      retryDelay,
      // A desktop console on office wifi, not a phone in a dead zone: a longer
      // stale time keeps a manager clicking between report tabs from re-querying
      // a 90-day window each time.
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

/*
 * The end of a session empties the cache, however it ended.
 *
 * The sign-out button did this for itself and the expiry path did not, which is
 * the worse half to miss: a token dies, the console drops to the login screen,
 * and the next person to sign in on that machine is served the previous
 * manager's outlets, users and reports out of the cache — for the 60 s stale
 * window on every query that has one, and immediately on any screen that reads
 * `data` before its refetch lands.
 *
 * Subscribed here rather than called from each sign-out site because it must
 * hold for sign-out sites that do not exist yet. A transition to a null token is
 * the only signal it needs, and every route out of a session produces one.
 */
useAuth.subscribe((state, previous) => {
  if (previous.accessToken !== null && state.accessToken === null) queryClient.clear();
});

const container = document.getElementById('root');
if (!container) throw new Error('root element missing');

createRoot(container).render(
  <StrictMode>
    {/*
      OUTSIDE EVERY PROVIDER, deliberately.

      This is the boundary that decides whether a thrown error costs a panel or
      the whole console (see ErrorBoundary.tsx). Putting it inside the providers
      would leave everything above it — and the providers' own construction —
      uncovered, which is precisely the region whose failure produces a blank
      page with nothing to read.

      It is safe out here because the fallback depends on no context: `Button`
      is a styled `<button>` and the rest is markup. A recovery screen that can
      itself fail for want of a provider is not a recovery screen.
    */}
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        {/*
          Icon defaults for the whole console — design-guidelines §8.

          `absoluteStrokeWidth` is the point of this provider. Without it a 16px
          icon inherits the same 2 stroke units as a 24px one and renders a third
          heavier, so a table-row icon out-weighs the semibold text beside it.
          With it, 1.75 means 1.75 CSS pixels at every size — close to the 1px
          hairline borders and to Manrope's own stem weight.
        */}
        <LucideProvider size={18} strokeWidth={1.75} absoluteStrokeWidth>
          <BrowserRouter>
            <App />
            {/*
              A SIBLING OF THE ROUTES, not a wrapper: a confirmation must outlive
              the navigation that follows the save it confirms. Inside the router
              so a future toast can carry a link, and inside `ErrorBoundary` so a
              toast that throws costs the console rather than the page.
            */}
            <Toaster />
          </BrowserRouter>
        </LucideProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
